import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Copy, ExternalLink, XCircle, IndianRupee, CheckCircle2, Clock, Sparkles, FileSignature, RefreshCw, RotateCcw } from 'lucide-react';
import { formatRenewalPeriod } from '../../lib/formatUtils';

const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(amt || 0);

/**
 * PaymentLinkDialog
 * -----------------
 * Generates a Razorpay Payment Link against a Hosting or AMC row and
 * lists previously-created links so operators can copy/share/cancel them.
 *
 * Props:
 *   open        — boolean
 *   onClose     — fn
 *   entityType  — 'hosting' | 'amc'
 *   entity      — the hosting/amc row object (for prefill + label)
 */
export function PaymentLinkDialog({ open, onClose, entityType, entity }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [amount, setAmount] = useState('');
  const [tenure, setTenure] = useState('1');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [description, setDescription] = useState('');
  // "Apply GST (18%)" — defaults ON so the operator can type the BASE
  // per-year price (₹100) and the dialog auto-grosses it up (₹118)
  // before sending to Razorpay. Saves a manual ×1.18 step on every
  // direct (no-quote) link. Auto-flipped OFF when a saved quote chip
  // is picked since quote prices already include GST.
  const [applyGst, setApplyGst] = useState(true);
  const GST_RATE = 0.18;
  // Discount controls — checkbox + single input with a ₹/% suffix
  // toggle. When checked, the operator types either a flat rupee
  // amount or a percentage; we clamp to [0, base] before computing
  // the after-discount total. Default OFF (no concession by default).
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [discountMode, setDiscountMode] = useState('percent'); // 'flat' | 'percent'
  const [discountValue, setDiscountValue] = useState('');
  // `descriptionTouched` flips to true the moment the operator types
  // anything in the Description textarea — at which point we stop
  // auto-syncing it to the tenure label so we don't clobber their
  // custom text. Reset on dialog open + when a quote chip is picked
  // (chip-set text is system-generated, not operator-typed).
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  // ---- Quote chip-picker state ----------------------------------------
  // Saved hosting quotes attached to this entity. Lets Ops pick the exact
  // tenure option from a previously-priced quote instead of retyping the
  // amount. AMC has no quote model → this stays empty for AMC.
  const [savedQuotes, setSavedQuotes] = useState([]);
  // Identifier of the currently-applied chip — `${quoteId}::${tenureYears}`.
  // Used purely for visual highlighting + audit pass-through on submit.
  const [activeChip, setActiveChip] = useState(null);
  const [showAllChips, setShowAllChips] = useState(false);

  const entityLabel = entity?.server_name || entity?.title || entity?.domain || 'this account';

  // Renewal-period anchor — Hosting tracks `expiry_date`, AMC tracks
  // `end_date`. If neither is set (lifetime hosting / open-ended AMC)
  // we skip the second description line gracefully.
  const renewalStartFrom = entity?.expiry_date || entity?.end_date || '';

  /**
   * Build the auto-description for a given tenure.
   *   Line 1: "<entityLabel> — Ny renewal"
   *   Line 2: "1st Jul 2026 to 30th Jun 2029"   (only when we know the
   *           current expiry — otherwise just line 1)
   * Quote-chip variant follows the same shape but uses the quote number
   * + plan label on line 1 (see `applyChip`).
   */
  const buildAutoDescription = (t) => {
    const head = `${entityLabel} — ${t} year${String(t) === '1' ? '' : 's'} renewal`;
    const period = formatRenewalPeriod(renewalStartFrom, t);
    return period ? `${head}\n${period}` : head;
  };

  const fetchLinks = useCallback(async () => {
    if (!entity?.id) return;
    setLoading(true);
    try {
      const r = await api.get('/payment-links', {
        params: { entity_type: entityType, entity_id: entity.id },
      });
      setLinks(r.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payment links');
    } finally {
      setLoading(false);
    }
  }, [entity?.id, entityType]);

  useEffect(() => {
    if (!open) return;
    // Reset form on each open and seed amount from the entity's stored rate.
    const seedAmount = entityType === 'hosting'
      ? (entity?.amount_per_year || entity?.cost || entity?.monthly_cost || '')
      : (entity?.annual_amount || entity?.amount || '');
    setAmount(seedAmount ? String(seedAmount) : '');
    setTenure('1');
    // Prefill recipient details from the entity's reminder_emails (which
    // is the operator's curated send-to list) — overridden below by the
    // linked client's billing details if any. This means Razorpay's
    // checkout page already has name + email + phone pre-populated, so
    // the customer doesn't have to re-type their phone number, and the
    // Razorpay receipt email/page shows the right customer info.
    setEmail((entity?.reminder_emails?.[0]) || '');
    setPhone('');
    setCustomerName(entity?.client_name || '');
    // Pre-fill description with the computed auto-text so the operator
    // sees exactly what'll go on the link/invoice. Stays auto-synced
    // until they manually edit (descriptionTouched flips it off).
    setDescription(buildAutoDescription('1'));
    setDescriptionTouched(false);
    setApplyGst(true);
    setApplyDiscount(false);
    setDiscountMode('percent');
    setDiscountValue('');
    setActiveChip(null);
    setShowAllChips(false);
    fetchLinks();
    // Hydrate from the linked client — billing emails > primary email,
    // and contact_person > company_name for the customer name field.
    if (entity?.client_id) {
      api.get(`/clients/${entity.client_id}`)
        .then(r => {
          const c = r.data?.data || r.data || {};
          // Pick the first TO billing email, then any billing email,
          // then the primary email — matches the email-priority used
          // by the invoice sender so the operator can trust this is the
          // same address that will get the tax invoice later.
          const billingTo = (c.billing_emails || []).find(b => (b.role || 'to') === 'to');
          const billingAny = (c.billing_emails || [])[0];
          const resolvedEmail =
            billingTo?.email || billingAny?.email || c.email || '';
          // Only override what's empty — operator may have already typed.
          setEmail(prev => prev || resolvedEmail);
          setPhone(prev => prev || (c.phone || ''));
          setCustomerName(prev => prev || c.contact_person || c.company_name || '');
        })
        .catch(() => { /* no linked client / 404 — keep entity defaults */ });
    }
    // Pre-fetch saved quotes — drives the chip-picker. Same flow for
    // both hosting (`/hosting/quotes`) and AMC (`/amc/quotes`); the
    // chip-flattener handles both shapes transparently.
    const quotesEndpoint = entityType === 'hosting' ? '/hosting/quotes'
                         : entityType === 'amc'     ? '/amc/quotes'
                         : null;
    const idParam = entityType === 'hosting' ? 'hosting_id'
                  : entityType === 'amc'     ? 'amc_id'
                  : null;
    if (quotesEndpoint && idParam && entity?.id) {
      api.get(quotesEndpoint, { params: { [idParam]: entity.id, limit: 10 } })
        .then(r => setSavedQuotes(r.data?.data || []))
        .catch(() => setSavedQuotes([]));   // 4xx/5xx → just hide the section
    } else {
      setSavedQuotes([]);
    }
  }, [open, entity, entityType, fetchLinks]);

  // Flatten saved quotes into a list of selectable {quote, option} pairs.
  // Memoized on `savedQuotes` reference so we don't re-flatten on every
  // keystroke.
  const quoteChips = useMemo(() => flattenQuoteChips(savedQuotes), [savedQuotes]);
  const visibleChips = showAllChips ? quoteChips : quoteChips.slice(0, 3);

  const applyChip = (chip) => {
    // The dialog form takes a PER-YEAR amount; the chip carries the
    // billable TOTAL for the chosen tenure (matches what the client saw
    // in the quote). We floor (per-year = total / tenure) so the
    // "Total billable" tile never exceeds the quoted amount even when
    // the quoted total isn't evenly divisible (e.g. ₹18,938/3 = 6312.67
    // → floor 6312 × 3 = 18,936; safer to undercharge by ≤₹2 than to
    // over-charge by ₹1 above the quoted price).
    const perYear = Math.floor((chip.totalAmount || 0) / Math.max(chip.tenureYears, 1));
    setAmount(String(perYear));
    setTenure(String(chip.tenureYears));
    {
      // Chip-style description: quote number + plan label + tenure +
      // computed renewal period (line 2). Matches the structure of
      // `buildAutoDescription` but with the quote-pinned heading.
      const head = `${chip.quoteNumber} · ${chip.planLabel || 'Hosting'} · ${chip.tenureYears} year${chip.tenureYears > 1 ? 's' : ''} renewal`;
      const period = formatRenewalPeriod(renewalStartFrom, chip.tenureYears);
      setDescription(period ? `${head}\n${period}` : head);
    }
    // Chip-set text is system-generated, not operator-typed — reset
    // the "touched" flag so picking another chip / clearing the chip
    // and changing tenure keeps the description in sync.
    setDescriptionTouched(false);
    setActiveChip(chip.chipId);
    // Quote prices already include GST → don't gross them up again.
    setApplyGst(false);
    // Quotes have their own discount baked into the total — clear any
    // dialog-side discount to avoid stacking concessions on top.
    setApplyDiscount(false);
    setDiscountValue('');
  };

  // Any manual edit to the form fields clears the chip selection — the
  // payment link is no longer "from" that quote. We also flip GST back
  // ON since a manual edit defaults to "operator typed the base price".
  const onAmountChange = (v) => { setAmount(v); setActiveChip(null); if (!applyGst) setApplyGst(true); };
  // On tenure change: clear the chip (no longer a quote-pinned link) and
  // auto-rewrite the description to match the new tenure — *unless* the
  // operator has manually edited the description (we'd clobber their
  // text otherwise).
  const onTenureChange = (v) => {
    setTenure(v);
    setActiveChip(null);
    if (!descriptionTouched) setDescription(buildAutoDescription(v));
  };
  // User typed in the description box → stop auto-syncing it from now on.
  const onDescriptionChange = (v) => {
    setDescription(v);
    setDescriptionTouched(true);
    setActiveChip(null);
  };

  /**
   * Breakdown of what hits Razorpay.
   *   base           = per-year × tenure
   *   discountAmount = clamp(discountValue, 0, base)   (0 when applyDiscount=false)
   *   afterDiscount  = base − discountAmount
   *   gstAmount      = afterDiscount × 0.18            (0 when applyGst=false)
   *   total          = afterDiscount + gstAmount
   *
   * Order matters: discount is applied to BASE before GST is computed
   * — matches Indian GST law (tax invoice records the post-discount
   * subtotal). The Razorpay `amount` we send is this `total`.
   */
  const breakdown = useMemo(() => {
    const amt = parseFloat(amount) || 0;
    const t = parseInt(tenure, 10) || 1;
    const base = Math.round(amt * t * 100) / 100;
    let discountAmount = 0;
    if (applyDiscount && base > 0) {
      const v = parseFloat(discountValue) || 0;
      if (discountMode === 'percent') {
        const pct = Math.max(0, Math.min(100, v));
        discountAmount = Math.round(base * (pct / 100) * 100) / 100;
      } else {
        discountAmount = Math.max(0, Math.min(base, v));
      }
    }
    const afterDiscount = Math.round((base - discountAmount) * 100) / 100;
    const gstAmount = applyGst ? Math.round(afterDiscount * GST_RATE * 100) / 100 : 0;
    const total = Math.round((afterDiscount + gstAmount) * 100) / 100;
    return {
      base: Math.round(base),
      discountAmount: Math.round(discountAmount),
      afterDiscount: Math.round(afterDiscount),
      gstAmount: Math.round(gstAmount),
      total: Math.round(total),
    };
  }, [amount, tenure, applyGst, applyDiscount, discountMode, discountValue]);

  const totalForTenure = breakdown.total;

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (!entity?.id) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid per-year amount'); return;
    }
    if (!email) {
      toast.error('Enter a recipient email'); return;
    }
    setCreating(true);
    const tid = toast.loading('Generating Razorpay link…');
    // If the operator picked a chip, peel off the quote id + tenure for
    // the audit trail. Manual edits clear `activeChip` so this is null
    // unless the chip was the actual source.
    const chipMeta = activeChip
      ? quoteChips.find(c => c.chipId === activeChip)
      : null;
    try {
      const r = await api.post('/payment-links', {
        entity_type: entityType,
        entity_id: entity.id,
        amount: totalForTenure,           // total billable amount = per-year × tenure
        tenure_years: parseInt(tenure, 10) || 1,
        recipient_email: email,
        recipient_phone: phone,
        recipient_name: customerName,
        description: description || undefined,
        notify_via: ['email'],
        from_quote_id: chipMeta?.quoteId || undefined,
        from_quote_tenure_years: chipMeta?.tenureYears || undefined,
        // Audit-trail hint — true when the operator typed a base price
        // and we auto-grossed-up; false when they unchecked the box
        // (typed value treated as already-inclusive).
        gst_inclusive: applyGst,
        gst_rate: applyGst ? GST_RATE : 0,
        // Discount audit trail. `discount_amount_inr` is the cash value
        // we actually subtracted from base (after clamping); `base_amount`
        // is the pre-discount per-year × tenure figure so a reconcile
        // script can reconstruct the math without re-doing the clamping.
        discount_applied: applyDiscount && breakdown.discountAmount > 0,
        discount_mode: applyDiscount ? discountMode : null,
        discount_value: applyDiscount ? (parseFloat(discountValue) || 0) : 0,
        discount_amount_inr: applyDiscount ? breakdown.discountAmount : 0,
        base_amount: breakdown.base,
      });
      toast.success('Payment link generated & emailed', { id: tid });
      // Auto-copy short URL for ops who'd rather paste into WhatsApp.
      try { await navigator.clipboard.writeText(r.data.data.short_url); } catch { /* clipboard may be blocked */ }
      fetchLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate link', { id: tid });
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async (link) => {
    const tid = toast.loading('Cancelling…');
    try {
      await api.post(`/payment-links/${link.id}/cancel`);
      toast.success('Link cancelled', { id: tid });
      fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed', { id: tid });
    }
  };

  // Manual reconcile — pulls the latest status from Razorpay for a
  // single link. Used when the user paid but our webhook didn't fire
  // (URL not configured, ngrok preview, signature mismatch, …). The
  // list endpoint already auto-reconciles on open, this is the explicit
  // refresh action so the user can re-check at any time.
  const [syncing, setSyncing] = useState({});
  const handleSync = async (link) => {
    setSyncing(s => ({ ...s, [link.id]: true }));
    const tid = toast.loading('Refreshing status from Razorpay…');
    try {
      const r = await api.post(`/payment-links/${link.id}/sync`);
      const updated = r.data?.data || {};
      if (r.data?.changed) {
        if (updated.status === 'paid') {
          const extended = updated.extended_to_date
            ? ` Tenure extended to ${new Date(updated.extended_to_date).toLocaleDateString('en-IN', { dateStyle: 'medium' })}.`
            : '';
          toast.success(`Marked as paid.${extended}`, { id: tid });
        } else {
          toast.success(`Status updated to "${updated.status}"`, { id: tid });
        }
      } else {
        toast.message('Still awaiting payment on Razorpay.', { id: tid });
      }
      fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed', { id: tid });
    } finally {
      setSyncing(s => ({ ...s, [link.id]: false }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="payment-link-dialog">
        <DialogHeader>
          <DialogTitle>Send Payment Link</DialogTitle>
          <DialogDescription>
            Generate a Razorpay payment link for <b>{entityLabel}</b> and email it to your client.
            They can pay 24/7 — no follow-ups needed.
          </DialogDescription>
        </DialogHeader>

        {/* ---- Quote chip-picker — only when saved quotes exist for this entity ---- */}
        {quoteChips.length > 0 && (
          <div
            className="space-y-2 border rounded-lg p-3 bg-muted/30"
            data-testid="paylink-quote-picker"
          >
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-amber-500" />
                Use saved quote
              </Label>
              {quoteChips.length > 3 && (
                <button
                  type="button"
                  onClick={() => setShowAllChips(v => !v)}
                  className="text-[11px] text-muted-foreground hover:text-primary"
                  data-testid="paylink-quote-toggle"
                >
                  {showAllChips ? 'Show less' : `Show all (${quoteChips.length})`}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {visibleChips.map(chip => {
                const isActive = activeChip === chip.chipId;
                return (
                  <button
                    key={chip.chipId}
                    type="button"
                    onClick={() => applyChip(chip)}
                    className={
                      'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors ' +
                      (isActive
                        ? 'bg-emerald-500/15 border-emerald-500 text-emerald-700 font-medium'
                        : 'bg-card hover:bg-accent border-border')
                    }
                    data-testid={`paylink-quote-chip-${chip.quoteId}-${chip.tenureYears}`}
                  >
                    {isActive && <CheckCircle2 className="h-3 w-3" />}
                    {!isActive && <FileSignature className="h-3 w-3 text-muted-foreground" />}
                    <span className="font-mono text-[11px] text-muted-foreground">{chip.quoteNumber}</span>
                    <span className="font-medium">·</span>
                    <span>{chip.label || `${chip.tenureYears}y`}</span>
                    <span className="font-medium">·</span>
                    <span className="font-semibold">{formatCurrency(chip.totalAmount)}</span>
                    {chip.isRecommended && <span className="ml-0.5 text-amber-500">★</span>}
                  </button>
                );
              })}
            </div>
            {activeChip && (
              <p className="text-[11px] text-emerald-600 mt-1">
                Amount &amp; tenure filled from quote · edit any field below to override.
              </p>
            )}
          </div>
        )}

        {/* ---- New link form ---- */}
        <form onSubmit={handleCreate} className="space-y-4 border-b pb-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Amount per year (₹)</Label>
              <div className="relative">
                <IndianRupee className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number" min="1" step="1"
                  value={amount}
                  onChange={(e) => onAmountChange(e.target.value)}
                  className="pl-8"
                  placeholder="e.g. 12000"
                  data-testid="paylink-amount-input"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tenure</Label>
              <Select value={tenure} onValueChange={onTenureChange}>
                <SelectTrigger data-testid="paylink-tenure-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 year</SelectItem>
                  <SelectItem value="2">2 years</SelectItem>
                  <SelectItem value="3">3 years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Apply-GST checkbox — defaults ON so the operator can type the
              base price and have GST auto-applied. Auto-flipped OFF when
              a saved quote chip is picked (quote prices already include
              GST). */}
          <label
            className="flex items-start gap-2 p-2.5 bg-sky-500/5 border border-sky-500/20 rounded-md cursor-pointer hover:bg-sky-500/10 transition-colors"
            data-testid="paylink-gst-toggle"
          >
            <Checkbox
              checked={applyGst}
              onCheckedChange={(v) => setApplyGst(v === true)}
              className="mt-0.5"
              data-testid="paylink-gst-checkbox"
            />
            <div className="text-xs flex-1 leading-tight">
              <span className="font-medium">Apply GST (18%) on entered amount</span>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {applyGst
                  ? 'Typed amount is the BASE (per-year) — GST will be added automatically.'
                  : 'Typed amount is treated as ALREADY INCLUSIVE of GST.'}
              </p>
            </div>
          </label>

          {/* Discount checkbox + single input with ₹/% suffix toggle.
              Applied to the BASE before GST so the tax invoice records
              the correct post-discount subtotal. Auto-cleared when a
              saved quote chip is picked. */}
          <div className="p-2.5 bg-amber-500/5 border border-amber-500/20 rounded-md">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="paylink-discount-toggle">
              <Checkbox
                checked={applyDiscount}
                onCheckedChange={(v) => setApplyDiscount(v === true)}
                className="mt-0.5"
                data-testid="paylink-discount-checkbox"
              />
              <div className="text-xs flex-1 leading-tight">
                <span className="font-medium">Apply discount on total</span>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Reduces the BASE before GST is computed. Used for one-off concessions / festive offers.
                </p>
              </div>
            </label>
            {applyDiscount && (
              <div className="mt-2.5 flex items-center gap-2">
                <div className="relative flex-1">
                  <Input
                    type="number"
                    min="0"
                    step={discountMode === 'percent' ? '0.5' : '1'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountMode === 'percent' ? 'e.g. 5' : 'e.g. 500'}
                    className="pr-20"
                    data-testid="paylink-discount-input"
                  />
                  {/* ₹/% segmented toggle pinned inside the input on the right */}
                  <div className="absolute right-1 top-1 bottom-1 flex items-center gap-0.5 bg-background border rounded-md p-0.5">
                    <button
                      type="button"
                      onClick={() => setDiscountMode('flat')}
                      className={
                        'px-2 py-0.5 text-[11px] rounded-sm transition-colors ' +
                        (discountMode === 'flat'
                          ? 'bg-amber-500/20 text-amber-700 font-semibold'
                          : 'text-muted-foreground hover:text-foreground')
                      }
                      data-testid="paylink-discount-mode-flat"
                    >₹</button>
                    <button
                      type="button"
                      onClick={() => setDiscountMode('percent')}
                      className={
                        'px-2 py-0.5 text-[11px] rounded-sm transition-colors ' +
                        (discountMode === 'percent'
                          ? 'bg-amber-500/20 text-amber-700 font-semibold'
                          : 'text-muted-foreground hover:text-foreground')
                      }
                      data-testid="paylink-discount-mode-percent"
                    >%</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Recipient name</Label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="e.g. Acme Corporation"
                data-testid="paylink-name-input"
              />
              <p className="text-[10px] text-muted-foreground">Shown on the Razorpay checkout page + receipt</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Recipient email</Label>
              <Input
                type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="billing@client.com"
                data-testid="paylink-email-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Phone (prefills Razorpay checkout)</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                data-testid="paylink-phone-input"
              />
              <p className="text-[10px] text-muted-foreground">Without this, the customer is asked to type it during checkout</p>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description (optional)</Label>
            <Textarea
              rows={2} value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder={`${entityLabel} — ${tenure} year${tenure === '1' ? '' : 's'} renewal`}
              data-testid="paylink-description-input"
            />
          </div>
          <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-md text-sm space-y-1">
            {(applyGst || applyDiscount) && breakdown.base > 0 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Base ({tenure}y × {formatCurrency(parseFloat(amount) || 0)})</span>
                <span data-testid="paylink-base-display">{formatCurrency(breakdown.base)}</span>
              </div>
            )}
            {applyDiscount && breakdown.discountAmount > 0 && (
              <>
                <div className="flex items-center justify-between text-xs text-amber-600">
                  <span>
                    − Discount
                    {discountMode === 'percent'
                      ? ` (${parseFloat(discountValue) || 0}%)`
                      : ''}
                  </span>
                  <span data-testid="paylink-discount-display">−{formatCurrency(breakdown.discountAmount)}</span>
                </div>
                {applyGst && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>= After discount</span>
                    <span data-testid="paylink-afterdiscount-display">{formatCurrency(breakdown.afterDiscount)}</span>
                  </div>
                )}
              </>
            )}
            {applyGst && breakdown.afterDiscount > 0 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>+ GST @ 18%</span>
                <span data-testid="paylink-gst-display">{formatCurrency(breakdown.gstAmount)}</span>
              </div>
            )}
            {(applyGst || applyDiscount) && breakdown.base > 0 && (
              <div className="h-px bg-emerald-500/20 my-1" />
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total to charge</span>
              <span className="font-semibold text-base" data-testid="paylink-total-display">
                {formatCurrency(totalForTenure)}
              </span>
            </div>
          </div>
          <Button
            type="submit"
            disabled={creating}
            className="w-full"
            data-testid="paylink-create-btn"
          >
            {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Generate &amp; Send Payment Link
          </Button>
        </form>

        {/* ---- History ---- */}
        <div className="space-y-2 max-h-[280px] overflow-y-auto">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold">PREVIOUS LINKS</Label>
            <div className="flex items-center gap-2">
              {loading && <Loader2 className="h-3 w-3 animate-spin" />}
              {links.length > 0 && !loading && (
                <button
                  type="button"
                  onClick={fetchLinks}
                  className="text-[11px] text-muted-foreground hover:text-primary flex items-center gap-1"
                  title="Pull latest status from Razorpay"
                  data-testid="paylink-refresh-all"
                >
                  <RefreshCw className="h-3 w-3" /> Refresh
                </button>
              )}
            </div>
          </div>
          {!loading && links.length === 0 && (
            <p className="text-xs text-muted-foreground">No payment links yet.</p>
          )}
          {links.map((l) => (
            <div
              key={l.id}
              className="border rounded-md p-3 flex items-center justify-between gap-3"
              data-testid={`paylink-row-${l.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{formatCurrency(l.amount)}</span>
                  <Badge variant="outline" className="text-[10px]">{l.tenure_years}y</Badge>
                  <StatusChip status={l.status} />
                  <span className="text-[11px] text-muted-foreground">{new Date(l.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                </div>
                <p className="text-xs text-muted-foreground truncate">{l.recipient_email}</p>
                <p className="text-[11px] font-mono truncate">{l.short_url}</p>
                {l.status === 'paid' && l.extended_to_date && (
                  <p className="text-[11px] text-emerald-600">
                    Renewed → {new Date(l.extended_to_date).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {(l.status === 'created' || l.status === 'paid' || l.status === 'partially_refunded') && (
                  <Button
                    size="icon" variant="ghost"
                    title={l.status === 'created' ? 'Refresh payment status' : 'Re-check refund status'}
                    onClick={() => handleSync(l)}
                    disabled={!!syncing[l.id]}
                    data-testid={`paylink-sync-${l.id}`}
                  >
                    {syncing[l.id]
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <RefreshCw className="h-4 w-4" />}
                  </Button>
                )}
                <Button
                  size="icon" variant="ghost" title="Copy URL"
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(l.short_url); toast.success('Copied'); }
                    catch { toast.error('Copy failed'); }
                  }}
                  data-testid={`paylink-copy-${l.id}`}
                ><Copy className="h-4 w-4" /></Button>
                <Button
                  size="icon" variant="ghost" title="Open in new tab"
                  asChild
                  data-testid={`paylink-open-${l.id}`}
                >
                  <a href={l.short_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
                {l.status === 'created' && (
                  <Button
                    size="icon" variant="ghost" title="Cancel"
                    onClick={() => handleCancel(l)}
                    data-testid={`paylink-cancel-${l.id}`}
                  ><XCircle className="h-4 w-4 text-red-500" /></Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="paylink-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusChip({ status }) {
  if (status === 'paid') return (
    <Badge className="text-[10px] bg-emerald-500/15 text-emerald-700 border-emerald-500/30 flex items-center gap-1">
      <CheckCircle2 className="h-3 w-3" /> Paid
    </Badge>
  );
  if (status === 'partially_refunded') return (
    <Badge className="text-[10px] bg-orange-500/15 text-orange-700 border-orange-500/30 flex items-center gap-1">
      <RotateCcw className="h-3 w-3" /> Partially refunded
    </Badge>
  );
  if (status === 'refunded') return (
    <Badge className="text-[10px] bg-slate-500/15 text-slate-700 border-slate-500/30 flex items-center gap-1">
      <RotateCcw className="h-3 w-3" /> Refunded
    </Badge>
  );
  if (status === 'cancelled') return (
    <Badge className="text-[10px] bg-red-500/10 text-red-600 border-red-500/30 flex items-center gap-1">
      <XCircle className="h-3 w-3" /> Cancelled
    </Badge>
  );
  if (status === 'expired') return (
    <Badge variant="outline" className="text-[10px]">Expired</Badge>
  );
  return (
    <Badge className="text-[10px] bg-amber-500/10 text-amber-700 border-amber-500/30 flex items-center gap-1">
      <Clock className="h-3 w-3" /> Awaiting payment
    </Badge>
  );
}

// --------------------------------------------------------------- helpers
// Plain (non-hook) helpers below — the React.useMemo wrappers live inside
// the component above. Naming intentionally avoids the `use*` prefix so
// the React rules-of-hooks linter doesn't flag them.

/**
 * Flattens an array of hosting_quotes into selectable chips, one per
 * tenure option (multi-tenure) or one per quote (legacy single-tenure).
 *
 * Stable ordering: keeps the API's createdAt-desc order, then within a
 * quote, by tenure ascending (1y, 2y, 3y).
 */
function flattenQuoteChips(quotes) {
  if (!Array.isArray(quotes) || quotes.length === 0) return [];
  const chips = [];
  for (const q of quotes) {
    const planLabel = q.plan_label || q.title || '';
    const quoteNumber = q.quote_number || q.id?.slice(-8).toUpperCase() || '';
    // Hosting quotes carry `tenure_options[]` (years). AMC quotes carry
    // `duration_options[]` (months). Normalise both to a year-equivalent
    // tenure for the chip and the dialog form.
    const tenureOpts = Array.isArray(q.tenure_options) ? q.tenure_options : [];
    const durationOpts = Array.isArray(q.duration_options) ? q.duration_options : [];

    if (tenureOpts.length > 0) {
      const sorted = [...tenureOpts].sort((a, b) => (a.tenure_years || 0) - (b.tenure_years || 0));
      for (const o of sorted) {
        const total = Number(o.total_amount ?? o.subtotal ?? 0);
        const ty = Number(o.tenure_years || 1);
        if (total <= 0 || ty <= 0) continue;
        chips.push({
          chipId: `${q.id}::y${ty}`,
          quoteId: q.id,
          quoteNumber, planLabel,
          tenureYears: ty,
          totalAmount: total,
          isRecommended: !!o.is_recommended,
          label: `${ty}y`,
        });
      }
    } else if (durationOpts.length > 0) {
      const sorted = [...durationOpts].sort((a, b) => (a.duration_months || 0) - (b.duration_months || 0));
      for (const o of sorted) {
        const total = Number(o.total_amount ?? o.subtotal ?? 0);
        const dm = Number(o.duration_months || 0);
        if (total <= 0 || dm <= 0) continue;
        // Map months → year-equivalent tenure for the dialog form.
        // For <12mo we still bill as a single tenure_years=1 line and
        // pass the full total; the per-year amount = total/1.
        const ty = Math.max(1, Math.round(dm / 12));
        chips.push({
          chipId: `${q.id}::m${dm}`,
          quoteId: q.id,
          quoteNumber, planLabel,
          tenureYears: ty,
          totalAmount: total,
          isRecommended: !!o.is_recommended,
          label: dm % 12 === 0 ? `${dm / 12}y` : `${dm}mo`,
        });
      }
    } else {
      // Legacy single-tenure quote: one chip for the whole quote.
      const total = Number(q.total_amount ?? q.subtotal ?? q.amount ?? 0);
      const ty = Number(q.tenure_years || 1);
      if (total > 0 && ty > 0) {
        chips.push({
          chipId: `${q.id}::y${ty}`,
          quoteId: q.id,
          quoteNumber, planLabel,
          tenureYears: ty,
          totalAmount: total,
          isRecommended: false,
          label: `${ty}y`,
        });
      }
    }
  }
  return chips;
}

export default PaymentLinkDialog;
